import type { AiProvider, AiCallOptions } from './provider.ts';
import type { AiTopupVerifyResponse } from './contract.ts';

interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Only an opaque order ID, scoped to this company's wallet in this tab. No card, amount or auth data. */
export function createPaymentRecoveryStore(walletPath: string, storage?: StorageLike) {
  const key = `sakhtar-ai-payment:${walletPath}`;
  return {
    read(): string | null {
      try {
        const id = storage?.getItem(key);
        return id && UUID.test(id) ? id : null;
      } catch {
        return null;
      }
    },
    remember(orderId: string) {
      if (!UUID.test(orderId)) return;
      try {
        storage?.setItem(key, orderId);
      } catch {
        /* server order history remains the recovery source */
      }
    },
    clear(orderId: string) {
      try {
        if (storage?.getItem(key) === orderId) storage.removeItem(key);
      } catch {
        /* best effort */
      }
    },
  };
}

export function browserPaymentRecoveryStore(walletPath: string) {
  try {
    return createPaymentRecoveryStore(walletPath, window.sessionStorage);
  } catch {
    return createPaymentRecoveryStore(walletPath);
  }
}

export type PaymentRecoveryStore = ReturnType<typeof createPaymentRecoveryStore>;

/** A retry never creates checkout or trusts the browser's Status as proof. */
export async function recoverWalletPayment(
  provider: Pick<AiProvider, 'verifyTopup' | 'reconcileTopup'>,
  href: string,
  store: PaymentRecoveryStore,
  options?: AiCallOptions,
): Promise<{ result: AiTopupVerifyResponse; completedUrl: string | null } | null> {
  const url = new URL(href);
  const authority = url.searchParams.get('Authority');
  const orderId = store.read();
  if (!authority && !orderId) return null;
  const result = authority
    ? await provider.verifyTopup(authority, url.searchParams.get('Status') ?? undefined, options)
    : await provider.reconcileTopup(orderId!, options);
  if (result.status === 'pending') return { result, completedUrl: null };
  // Only a conclusive backend result clears recovery data. A rejection/timeout keeps it intact.
  store.clear(result.orderId);
  url.searchParams.delete('Authority');
  url.searchParams.delete('Status');
  return { result, completedUrl: `${url.pathname}${url.search}${url.hash}` };
}
